import React from 'react';
import { motion } from 'framer-motion';
interface Props {
  open: boolean;
  /** Scene revealed behind the doors */
  children: ReactNode;
  /** Optional content rendered centered ON the closed doors (title, CTA) */
  overlay?: ReactNode;
}
/**
 * Reusable white double doors with gold handles that swing open in 3D
 * to reveal whatever is passed as children. Wonder of Life signature transition.
 */
export function SpaDoors({
  open,
  children,
  overlay
}: Props) {
  return <div className="absolute inset-0 overflow-hidden" style={{
    perspective: 1800
  }} data-id="element-211">
      {/* Revealed scene behind the doors */}
      <div className="absolute inset-0" data-id="element-212">{children}</div>

      {/* Soft warm glow that grows from the seam as doors part */}
      <motion.div animate={{
      opacity: open ? 0 : 0.9,
      scaleX: open ? 1.6 : 1
    }} transition={{
      duration: 1.8,
      ease: [0.65, 0, 0.35, 1]
    }} className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-32 pointer-events-none z-10" style={{
      background: 'linear-gradient(to right, transparent, rgba(255,228,170,0.5), transparent)',
      filter: 'blur(20px)'
    }} data-id="element-213" />

      {/* Left door */}
      <motion.div initial={false} animate={{
      rotateY: open ? -105 : 0
    }} transition={{
      duration: 1.9,
      ease: [0.65, 0, 0.35, 1]
    }} className="absolute top-0 left-0 w-1/2 h-full z-20" style={{
      transformOrigin: 'left center',
      transformStyle: 'preserve-3d',
      background: 'linear-gradient(105deg, #ffffff 0%, #f4f1ec 50%, #e9e3d8 100%)',
      boxShadow: 'inset -3px 0 12px rgba(0,0,0,0.12), inset 0 0 60px rgba(220,200,150,0.12)'
    }} data-id="element-214">
        {/* Recessed panels */}
        <div className="absolute inset-[12%] flex flex-col gap-[8%]" data-id="element-215">
          <div className="flex-1 border border-black/5 rounded-sm shadow-inner" data-id="element-216" />
          <div className="flex-1 border border-black/5 rounded-sm shadow-inner" data-id="element-217" />
        </div>
        {/* Gold handle */}
        <div className="absolute right-[8%] top-1/2 -translate-y-1/2 w-2.5 h-14 rounded-full" style={{
        background: 'linear-gradient(to right, #b08e4e, #e8c97a 45%, #fff3d4 55%, #b08e4e)',
        boxShadow: '0 0 8px rgba(201,169,106,0.6), 0 2px 4px rgba(0,0,0,0.2)'
      }} data-id="element-218" />
      </motion.div>

      {/* Right door */}
      <motion.div initial={false} animate={{
      rotateY: open ? 105 : 0
    }} transition={{
      duration: 1.9,
      ease: [0.65, 0, 0.35, 1]
    }} className="absolute top-0 right-0 w-1/2 h-full z-20" style={{
      transformOrigin: 'right center',
      transformStyle: 'preserve-3d',
      background: 'linear-gradient(255deg, #ffffff 0%, #f4f1ec 50%, #e9e3d8 100%)',
      boxShadow: 'inset 3px 0 12px rgba(0,0,0,0.12), inset 0 0 60px rgba(220,200,150,0.12)'
    }} data-id="element-219">
        <div className="absolute inset-[12%] flex flex-col gap-[8%]" data-id="element-220">
          <div className="flex-1 border border-black/5 rounded-sm shadow-inner" data-id="element-221" />
          <div className="flex-1 border border-black/5 rounded-sm shadow-inner" data-id="element-222" />
        </div>
        <div className="absolute left-[8%] top-1/2 -translate-y-1/2 w-2.5 h-14 rounded-full" style={{
        background: 'linear-gradient(to right, #b08e4e, #fff3d4 45%, #e8c97a 55%, #b08e4e)',
        boxShadow: '0 0 8px rgba(201,169,106,0.6), 0 2px 4px rgba(0,0,0,0.2)'
      }} data-id="element-223" />
      </motion.div>

      {/* Center seam line (visible while closed) */}
      <motion.div animate={{
      opacity: open ? 0 : 1
    }} transition={{
      duration: 0.6
    }} className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-black/10 z-20 pointer-events-none" data-id="element-224" />

      {/* Overlay content on the closed doors */}
      {overlay && <motion.div animate={{
      opacity: open ? 0 : 1
    }} transition={{
      duration: open ? 0.5 : 1,
      delay: open ? 0 : 0.2
    }} className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none" data-id="element-225">
          <div className="pointer-events-auto" data-id="element-226">{overlay}</div>
        </motion.div>}
    </div>;
}